import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { getProjectSchema, projectToFirestore } from '@parallel-fluidics/shared/models/projects';
import { useQuery } from '@tanstack/react-query';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { useUserData } from '../../providers/userDataProvider';
import { incrementString } from '../../utils/format';

export default function AddProjectModal({
  open,
  onClose,
  onSuccess = null,
  isInternal = false,
}) {
  const { user } = useUserData();

  const [debouncedProjectId, setDebouncedProjectId] = useState(null);
  const [debouncedPartId, setDebouncedPartId] = useState(null);
  const [foundDuplicate, setFoundDuplicate] = useState(null);
  const [isPartIdValid, setIsPartIdValid] = useState(false);

  const initialValues = useMemo(() => {
    const values = {
      name: '',
      notes: '',
      description: '',
      isInternal,
      uid: user.id,
    };
    if (!isInternal) {
      values.filename = '';
      values.partId = '';
    }
    return values;
  }, [isInternal, user.id]);

  const { data: isExistingProjectId, isFetching: isValidatingProject } = useQuery({
    queryKey: ['validateProjectId', debouncedProjectId],
    queryFn: async () => {
      const nameQuery = query(
        collection(getFirestore(), 'projects'),
        where('name', '==', debouncedProjectId),
        limit(1),
      );
      const projectsSnapshot = await getDocs(nameQuery);
      return !projectsSnapshot.empty;
    },
    enabled: !!debouncedProjectId && (
      isInternal
        ? debouncedProjectId.length === 4
        : debouncedProjectId.length === 6
    ),
  });

  const {
    values, errors, handleSubmit, setFieldValue, validateForm, isSubmitting,
  } = useFormik({
    initialValues,
    validateOnBlur: false,
    validationSchema: getProjectSchema(isInternal),
    validate: (validateValues) => {
      const validateErrors = {};
      if (isExistingProjectId) {
        validateErrors.name = 'Project ID already exists. Please choose a different one.';
      }
      if (!isInternal && !isPartIdValid) {
        validateErrors.partId = 'Invalid part ID';
      }
      if (!validateValues.notes && foundDuplicate) {
        validateErrors.notes = 'Found a project for this part ID. Add a note to indicate why a new project ID is required.';
      }
      return validateErrors;
    },
    onSubmit: async (submitValues) => {
      try {
        const docData = projectToFirestore(submitValues, serverTimestamp);
        await addDoc(collection(getFirestore(), 'projects'), docData);
        if (typeof onSuccess === 'function') onSuccess();
        onClose();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error adding document:', error);
      }
    },
  });

  // load next project id
  useEffect(() => {
    async function fetchLatestProject() {
      const firstCQuery = query(
        collection(getFirestore(), 'projects'),
        where('isInternal', '==', isInternal),
        orderBy('name', 'desc'),
        limit(1),
      );
      const firstCProject = await getDocs(firstCQuery).then((snapshot) => {
        if (snapshot.empty) return null;
        return snapshot.docs[0].data();
      });
      const defaultProjectId = isInternal ? 'P000' : 'C00000';
      const name = incrementString(firstCProject?.name || defaultProjectId);
      setFieldValue('name', name);
    }
    fetchLatestProject();
  }, [isInternal, setFieldValue]);

  useEffect(() => {
    validateForm();
  }, [isPartIdValid, isExistingProjectId, validateForm]);

  useEffect(() => {
    if (!values.partId) return undefined;
    const handler = setTimeout(() => {
      setDebouncedPartId(values.partId);
    }, 500);

    return () => clearTimeout(handler);
  }, [values.partId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedProjectId(values.name);
    }, 500);

    return () => clearTimeout(handler);
  }, [values.name]);

  const { isFetching } = useQuery({
    queryKey: ['getPartById', debouncedPartId],
    queryFn: async () => {
      const partDocRef = doc(getFirestore(), 'parts', debouncedPartId);

      const projectDocRef = query(
        collection(getFirestore(), 'projects'),
        where('partId', '==', debouncedPartId),
        limit(1),
      );

      const [partDocSnap, projectSnap] = await Promise.all([
        getDoc(partDocRef),
        getDocs(projectDocRef),
      ]);

      if (!projectSnap.empty) {
        const project = projectSnap.docs[0].data();
        setFoundDuplicate(project);
      } else {
        setFoundDuplicate(null);
      }

      if (partDocSnap.exists()) {
        const part = partDocSnap.data();
        setFieldValue('filename', part.filename);
        setIsPartIdValid(true);
      } else {
        setIsPartIdValid(false);
      }

      return null;
    },
    enabled: !!debouncedPartId,
  });

  return (
    <Modal
      onDismiss={onClose}
      visible={open}
      header={isInternal ? 'Create an internal project' : 'Create a customer project'}
    >
      <form onSubmit={handleSubmit}>
        <SpaceBetween direction="vertical" size="s">
          {foundDuplicate && (
            <Alert statusIconAriaLabel="Warning" type="warning">
              Found a project with the same part ID:
              {' '}
              <a href={foundDuplicate.partId ? `/part/${foundDuplicate.partId}` : `${foundDuplicate.adminLink}`} target="_blank" rel="noreferrer">{foundDuplicate.name}</a>
            </Alert>
          )}
          <FormField
            label="Project Id"
            errorText={errors.name}
          >
            <Input
              value={values.name}
              onChange={({ detail }) => { setFieldValue('name', detail.value); }}
            />
          </FormField>
          {isInternal ? (
            <FormField
              label="Description"
              errorText={errors.description}
            >
              <Input
                value={values.description}
                onChange={({ detail }) => { setFieldValue('description', detail.value); }}
              />
            </FormField>
          ) : (
            <>
              <FormField
                label="Part ID"
                errorText={errors.partId}
              >
                <Input
                  value={values.partId}
                  onChange={({ detail }) => { setFieldValue('partId', detail.value); }}
                />
              </FormField>
              <FormField
                label="File name"
                errorText={errors.filename}
              >
                <Input
                  value={values.filename}
                  onChange={({ detail }) => { setFieldValue('filename', detail.value); }}
                />
              </FormField>
            </>
          )}
          <FormField
            label="Notes"
            errorText={errors.notes}
          >
            <Textarea
              value={values.notes}
              onChange={({ detail }) => { setFieldValue('notes', detail.value); }}
            />
          </FormField>

          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" formAction="none" onClick={onClose}>Cancel</Button>
              <Button variant="primary" type="submit" loading={isSubmitting || isFetching || isValidatingProject}>Create</Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </form>
    </Modal>
  );
}

AddProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  isInternal: PropTypes.bool,
};
